import React from "react";
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {ChangePasswordRequest, ChangePasswordRequestFields} from "../../../../../types/user/authData";
import {Button} from "../../../../../components/forms";
import {HttpAuth} from "src/http/user/httpAuth";
import useAxios from "../../../../../hooks/useAxios";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveIcon from "@mui/icons-material/Save";
import toast from "react-hot-toast";
import {z} from "../../../../../lib";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import { ControlledPasswordField } from "src/components/forms/ControlledPasswordField";

interface ChangePasswordDialogProps {
    open: boolean,
    onClose: () => void
}

enum ChangePasswordRequestFormFields {
    NewPasswordConfirm = 'passwordNuevaConfirmar'
}

interface ChangePasswordRequestForm extends ChangePasswordRequest {
    [ChangePasswordRequestFormFields.NewPasswordConfirm]: string
}


function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
    const { t } = useTranslation();
    const { fetchData } = useAxios();

    const schemaChangePasswordForm = z.object({
        [ChangePasswordRequestFields.CurrentPassword]: z.any().refine(val => val !== undefined && val !== null, { message: "Required", }),
        [ChangePasswordRequestFields.NewPassword]: z.string(),
        [ChangePasswordRequestFormFields.NewPasswordConfirm]: z.string()
    })
        .superRefine(({ passwordNueva, passwordNuevaConfirmar }, ctx) => {
            if (passwordNueva !== passwordNuevaConfirmar) {
                ctx.addIssue({
                    code: "custom",
                    message: t("ContraseniaNuevaNoCoincide") ?? "",
                    path: [ChangePasswordRequestFormFields.NewPasswordConfirm]
                });
            }
        });
    const { control, handleSubmit, reset } = useForm<ChangePasswordRequestForm>({
        resolver: zodResolver(schemaChangePasswordForm)
    });
        
    const handleClose = () => {
        onClose();
        reset();
    }
    
    const closeSuccessfully = () => {
        toast.success(t("ContraseniaActualizadaExitosamente"));
        handleClose();
    }
        
    const onHandleSubmit = (data: ChangePasswordRequestForm) => {
        fetchData(
            () => HttpAuth.changePassword(data[ChangePasswordRequestFields.CurrentPassword], data[ChangePasswordRequestFields.NewPassword]),
            true
        )
            .then(closeSuccessfully)
            .catch(() => {
                console.error('hubo un error')
            });
    };
    
    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{t("CambiarContrasenia")}</DialogTitle>
            
            <form onSubmit={handleSubmit(onHandleSubmit)}>
                <DialogContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <ControlledPasswordField
                                control={control}
                                name={ChangePasswordRequestFields.CurrentPassword}
                                label={t("ContraseniaActual")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ControlledPasswordField
                                control={control}
                                name={ChangePasswordRequestFields.NewPassword}
                                label={t("ContraseniaNueva")}
                                showRules
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <ControlledPasswordField
                                control={control}
                                name={ChangePasswordRequestFormFields.NewPasswordConfirm}
                                label={t("ContraseniaNuevaRepetir")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <DialogActions>
                    <Button
                        variant='outlined'
                        color='primary'
                        size='medium'
                        startIcon={<CloseRoundedIcon />}
                        onClick={handleClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant='contained'
                        color='primary'
                        size='medium'
                        type='submit'
                        startIcon={<SaveIcon />}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );    
}



export default ChangePasswordDialog;